import { useLocation, Navigate } from 'react-router-dom';
import styles from './ConfirmOTP.module.css';
import { Helmet } from 'react-helmet';
import { Form, Formik, Field, ErrorMessage } from 'formik';
import { allAuth } from '@round/api';
import FormField from 'ui/DataEntry/FormField/FormField';
import Label from 'ui/DataEntry/Label/Label';
import Button from 'ui/Buttons/Button/Button';
import { useContext } from 'react';
import { AuthContext } from 'Modules/Auth/contexts/AuthContext';
import { useCountdown } from '@round/utils';
import useResendCode from './useResendCode';

const OTP_RESEND_COUNTDOWN_SECONDS = 30;

const ConfirmOTP = () => {
    const location = useLocation();
    const { sessionToken } = (location.state || {}) as { sessionToken?: string; email?: string };
    const authContext = useContext(AuthContext);
    const nextUrl = new URLSearchParams(location.search).get('next') ?? '/';
    const { remainingTimeFormatted, isExpired, reset } = useCountdown(OTP_RESEND_COUNTDOWN_SECONDS, {
        autoStart: true,
    });
    const {
        isLoading: isResending,
        isError: isResendError,
        errorMessage: resendErrorMessage,
        resendCode,
    } = useResendCode();

    if (!sessionToken) {
        return <Navigate to="/login/" replace />;
    }

    if (authContext.authenticated) {
        return <Navigate to={nextUrl} replace />;
    }

    return (
        <>
            <Helmet title="Confirm code | round" />
            <div className={styles.container}>
                <h1 className={styles.heading}>Check your email</h1>
                <Formik
                    initialValues={{ otp: '' }}
                    onSubmit={async (values, { setFieldError }) => {
                        if (typeof authContext.setTokens !== 'function') {
                            return;
                        }

                        try {
                            const response = await allAuth.confirmCode(values.otp, sessionToken);
                            if (response.status === 200 && response.meta.is_authenticated) {
                                authContext.setTokens({ access: response.meta.access, refresh: response.meta.refresh });
                                return;
                            }

                            if (response.status === 400) {
                                setFieldError('otp', response.errors[0]?.message ?? 'Invalid code');
                                return;
                            }

                            setFieldError('otp', 'Could not confirm code');
                        } catch {
                            setFieldError('otp', 'Could not confirm code');
                        }
                    }}
                >
                    {({ isSubmitting }) => (
                        <Form>
                            <FormField>
                                <Label htmlFor="otp">Verification code</Label>
                                <Field
                                    tabIndex={1}
                                    id="otp"
                                    name="otp"
                                    type="text"
                                    placeholder="Enter your code"
                                    className={styles.input}
                                />
                                <ErrorMessage name="otp">
                                    {(msg) => <span className={styles.error}>{msg}</span>}
                                </ErrorMessage>
                            </FormField>

                            {!isExpired && (
                                <p className={styles.resendCountdown}>
                                    You'll be able to request another code in {remainingTimeFormatted}
                                </p>
                            )}

                            {isExpired && isResendError && <span className={styles.error}>{resendErrorMessage}</span>}

                            {isExpired && !isResendError && (
                                <Button
                                    className={styles.resend}
                                    htmlType="button"
                                    type="link"
                                    color="primary"
                                    loading={isResending}
                                    onClick={async () => {
                                        const success = await resendCode(sessionToken);
                                        if (success) {
                                            reset();
                                        }
                                    }}
                                >
                                    Resend code
                                </Button>
                            )}

                            <Button
                                tabIndex={2}
                                loading={isSubmitting}
                                className={styles.submit}
                                htmlType="submit"
                                type="filled"
                                color="black"
                            >
                                Sign in
                            </Button>

                            <p className={styles.spamHint}>Can't find it? It may have landed in your spam folder.</p>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    );
};

export default ConfirmOTP;
